import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Hero from "../components/Hero";
import "../Style/Home.css";

// Import local video asset correctly for modern bundlers (Vite/Webpack)
import complianceVideo from "../assets/compliance_businessmanwebp_ma.mp4";

// Import reviews from separate file
import { googleReviews } from "../pages/review.js";

import {
  FaAward,
  FaCertificate,
  FaCheckCircle,
  FaClipboardCheck,
  FaCogs,
  FaFileSignature,
  FaGlobeAsia,
  FaHandshake,
  FaHospital,
  FaIndustry,
  FaMapMarkerAlt,
  FaPlus,
  FaMinus,
  FaQuoteLeft,
  FaRegBuilding,
  FaSchool,
  FaSearch,
  FaShieldAlt,
  FaShip,
  FaUsers,
  FaArrowRight,
  FaClock,
  FaUserTie,
  FaHeadset,
  FaFileContract,
  FaRocket,
  FaStar,
  FaExternalLinkAlt,
  FaYoutube,
  FaPlayCircle,
  FaQuestionCircle,
  FaWhatsapp,
  FaComments,
  FaTimes,
  FaPaperPlane
} from "react-icons/fa";

const faqData = [
  {
    question: "How long does ISO certification take for a Maldivian resort or business?",
    answer: "The timeline depends on your organization size, current operational readiness, and the specific standard. Resort implementations are carefully timed around peak/off-peak seasons to ensure minimal disruption to guest operations."
  },
  {
    question: "Do you help with local documentation and compliance?",
    answer: "Yes, we provide complete support for policy creation, SOPs, manuals, forms, and audit-ready records that comply with international ISO criteria as well as local Maldivian regulatory bodies."
  },
  {
    question: "Which industries in the Maldives do you support?",
    answer: "We heavily support Luxury Resorts, Liveaboards & Marine Transport, Construction & Infrastructure projects, Fisheries, Healthcare facilities, and Local Island councils."
  },
  {
    question: "Can you help before the final external certification audit?",
    answer: "Yes, we perform rigorous gap analysis, internal mock audits, and corrective action checks to ensure your team is completely confident before the international registrar arrives."
  }
];

const prestigiousClients = [
  { name: "HP", logo: "https://www.ascentinspecta.com/img/client/HP.png" },
  { name: "Vissco", logo: "https://www.ascentinspecta.com/img/client/Vissco.jpeg" },
  { name: "Mespack", logo: "https://www.ascentinspecta.com/img/client/mespack.jpeg" },
  { name: "Meeraki", logo: "https://www.ascentinspecta.com/img/client/Meeraki.png" },
  { name: "iBall", logo: "https://www.ascentinspecta.com/img/client/iball-logo.png" },
  { name: "Enet", logo: "https://www.ascentinspecta.com/img/client/enet.png" },
  { name: "Butterfly", logo: "https://www.ascentinspecta.com/img/client/buffterfly.png" },
  { name: "Western Coast", logo: "https://www.ascentinspecta.com/img/client/westerncoast.png" },
  { name: "Orient", logo: "https://www.ascentinspecta.com/img/client/orient.jpeg" },
  { name: "RPL", logo: "https://www.ascentinspecta.com/img/client/rpl.jpeg" },
  { name: "JK", logo: "https://www.ascentinspecta.com/img/client/jk.jpg" },
  { name: "BL", logo: "https://www.ascentinspecta.com/img/client/bl.jpg" },
  { name: "JAK", logo: "https://www.ascentinspecta.com/img/client/jak.png" }
];

const channelVideos = [
  {
    title: "ISO 13485 Quality Management System for Medical Devices",
    duration: "1:54",
    videoId: "DoWuHND3kBw",
    url: "https://youtu.be/DoWuHND3kBw"
  },
  {
    title: "ISO 20000 | IT Service Management Guide",
    duration: "1:11",
    videoId: "eFylRfiNxHg",
    url: "https://youtu.be/eFylRfiNxHg"
  },
  {
    title: "From ISO 9001:2015 to ISO 9001:2026: What's Changing",
    duration: "1:11",
    videoId: "FU2tFl7i3Ms",
    url: "https://youtu.be/FU2tFl7i3Ms"
  }
];

const whyChooseUsFeatures = [
  {
    icon: <FaUserTie />,
    title: "Localized Industry Experts",
    desc: "Our auditors understand the unique operational nuances of island resorts, marine transport, and remote supply chains.",
    highlight: "Atoll-Ready Expertise",
    image: "https://plus.unsplash.com/premium_photo-1725408034958-e0b4025deda6?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MXx8TG9jYWxpemVkJTIwSW5kdXN0cnklMjBFeHBlcnRzfGVufDB8fDB8fHww"
  },
  {
    icon: <FaFileContract />,
    title: "100% Tailored SOP Architecture",
    desc: "We build fully customized compliance manuals and digital documentation, avoiding generic, cookie-cutter templates.",
    highlight: "Zero Fluff Policy",
    image: "https://plus.unsplash.com/premium_photo-1770966188124-6879b822c311?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1yZWxhdGVkfDI3fHx8ZW58MHx8fHx8"
  },
  {
    icon: <FaClock />,
    title: "Accelerated Timelines",
    desc: "Structured milestone execution designed to minimize operational downtime and align with peak/off-peak seasons.",
    highlight: "Fast-Track Audit",
    image: "https://media.istockphoto.com/id/2190688121/photo/time-management-business-strategy-concept-businessman-planning-schedule-and-targets.webp?a=1&b=1&s=612x612&w=0&k=20&c=XkVfLOprdRJm70lJpyD0UAZkTnkAPaCnBRSm9ICMeBc="
  },
  {
    icon: <FaShieldAlt />,
    title: "Guaranteed Audit Success",
    desc: "Our pre-audit mock tests identify non-conformances early, ensuring a smooth, stress-free external audit.",
    highlight: "99% Pass Rate",
    image: "https://plus.unsplash.com/premium_photo-1664910070114-33ab91630e46?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTE3fHxHdWFyYW50ZWVkJTIwQXVkaXQlMjBTdWNjZXNzfGVufDB8fDB8fHww"
  },
  {
    icon: <FaGlobeAsia />,
    title: "International Recognition",
    desc: "Accredited frameworks recognized globally, helping Maldivian brands build trust with international clients and guests.",
    highlight: "Globally Valid",
    image: "https://images.unsplash.com/photo-1763811939066-e984d42551e7?q=80&w=1065&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1yZWxhdGVkfHx8fGVufDB8fHx8fA%3D%3D"
  },
  {
    icon: <FaHeadset />,
    title: "365-Day Support",
    desc: "Long-term partnership with yearly surveillance review assistance, internal staff training, and compliance tracking.",
    highlight: "Year-Round Help",
    image: "https://media.istockphoto.com/id/1434742171/photo/laptop-ppt-presentation-business-meeting-and-team-working-on-review-for-new-digital-website.webp?a=1&b=1&s=612x612&w=0&k=20&c=QGR4lIXS1P30qiZnhfq5cKMKLba1N3QGjWT1e012cYU="
  }
];

const processSteps = [
  {
    step: "01",
    title: "On-Site Gap Analysis Audit",
    shortTitle: "Gap Analysis",
    desc: "Our senior compliance engineers conduct an exhaustive physical and digital operational audit across your resort or corporate infrastructure to identify non-conformances.",
    badge: "In-Depth Assessment",
    icon: <FaSearch />,
    image: "https://images.unsplash.com/photo-1573843981267-be1999ff37cd?auto=format&fit=crop&w=1000&q=80",
    features: [
      "Infrastructure vulnerability check",
      "Operational readiness blueprint",
      "Complete gap report in 48 hours"
    ]
  },
  {
    step: "02",
    title: "Tailored Documentation & SOPs",
    shortTitle: "Custom Manuals",
    desc: "We build custom policy control manuals, dynamic risk logs, and standardized operating procedures (SOPs) explicitly tailored for Maldivian regulations.",
    badge: "Custom SOP Architecture",
    icon: <FaFileSignature />,
    image: "https://images.unsplash.com/photo-1512100356356-de1b84283e18?auto=format&fit=crop&w=1000&q=80",
    features: [
      "100% custom policy generation",
      "Local Maldivian authority compliance",
      "Digital cloud documentation vault"
    ]
  },
  {
    step: "03",
    title: "Internal Simulation Mock Audits",
    desc: "We stress-test your team with full-scale mock audits and practical simulation exercises to verify that your personnel can execute protocols flawlessly.",
    shortTitle: "Mock Testing",
    badge: "Zero-Risk Pre-Test",
    icon: <FaClipboardCheck />,
    image: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&w=1000&q=80",
    features: [
      "Simulated external audit stress test",
      "Corrective action plan implementation",
      "Staff training & role validation"
    ]
  },
  {
    step: "04",
    title: "International Certification & Award",
    shortTitle: "Final Certification",
    desc: "We provide on-site strategic liaison support while international registrars perform your formal certification audit, securing your accredited ISO credentials.",
    badge: "Global Accreditation",
    icon: <FaCertificate />,
    image: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1000&q=80",
    features: [
      "100% external audit pass guarantee",
      "Official ISO certificate issuance",
      "Continuous annual surveillance review"
    ]
  }
];

function FaqItem({ item, index, openIndex, setOpenIndex }) {
  const isOpen = openIndex === index;

  return (
    <div className={`faq-modern-card ${isOpen ? "open" : ""}`}>
      <button
        className="faq-modern-question"
        onClick={() => setOpenIndex(isOpen ? null : index)}
        aria-expanded={isOpen}
      >
        <span className="faq-modern-q-text">
          <span className="faq-modern-num">{String(index + 1).padStart(2, "0")}</span>
          {item.question}
        </span>
        <span className="faq-modern-toggle-icon">
          {isOpen ? <FaMinus /> : <FaPlus />}
        </span>
      </button>
      <div className="faq-modern-answer">
        <p>{item.answer}</p>
      </div>
    </div>
  );
}

function Home() {
  const [openIndex, setOpenIndex] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  
  // Review Carousel State
  const [activeReviewIndex, setActiveReviewIndex] = useState(0);

  // Chatbot states
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { sender: "bot", text: "Hello! Welcome to Ascent Inspecta. How can we help you with ISO certification today?" }
  ]);
  const [inputMessage, setInputMessage] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setActiveTab((prevTab) => (prevTab + 1) % processSteps.length);
    }, 1000);

    return () => clearInterval(interval);
  }, [isPaused]);

  // Auto-rotate reviews every 5 seconds
  useEffect(() => {
    const reviewInterval = setInterval(() => {
      setActiveReviewIndex((prevIndex) => (prevIndex + 1) % googleReviews.length);
    }, 5000);

    return () => clearInterval(reviewInterval);
  }, []);

  // Handle Chatbot Message Submission
  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const userText = inputMessage;
    const newMessages = [...chatMessages, { sender: "user", text: userText }];
    setChatMessages(newMessages);
    setInputMessage("");

    setTimeout(() => {
      let botReply = "Thank you for your inquiry. For immediate assistance with our experts, you can also connect directly via WhatsApp below!";
      
      const lower = userText.toLowerCase();
      if (lower.includes("iso") || lower.includes("certification") || lower.includes("cost") || lower.includes("time")) {
        botReply = "ISO certification timelines depend on your industry and size. Would you like to connect with our senior consultant on WhatsApp at +91 9867180395?";
      } else if (lower.includes("hello") || lower.includes("hi")) {
        botReply = "Hello there! Feel free to ask about ISO 9001, 14001, 22000, or click our WhatsApp button to chat instantly!";
      }

      setChatMessages((prev) => [...prev, { sender: "bot", text: botReply }]);
    }, 1000);
  };

  const featuredServices = [
    {
      title: "Resort Quality",
      code: "ISO 9001",
      desc: "Premium Hospitality & Operations Management System",
      icon: <FaAward />,
      image: "https://images.unsplash.com/photo-1573843981267-be1999ff37cd?auto=format&fit=crop&w=800&q=80",
      className: "card-variant-1",
      id: "iso-9001"
    },
    {
      title: "Eco-Tourism & Marine",
      code: "ISO 14001",
      desc: "Environmental Systems for Fragile Island Ecosystems",
      icon: <FaGlobeAsia />,
      image: "https://images.unsplash.com/photo-1647192235579-9c2dcd72606d?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTA3fHxFY28tVG91cmlzbSUyMCUyMCUyME1hcmluZXxlbnwwfHwwfHx8MA%3D%3D",
      className: "card-variant-2",
      id: "iso-14001"
    },
    {
      title: "Resort Food Safety",
      code: "ISO 22000",
      desc: "HACCP & Food Safety Frameworks for Fine Dining & Supply Chains",
      icon: <FaShieldAlt />,
      image: "https://images.unsplash.com/photo-1616734755909-bb016ce64930?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8UmVzb3J0JTIwRm9vZCUyMFNhZmV0eXxlbnwwfHwwfHx8MA%3D%3D",
      className: "card-variant-3",
      id: "iso-22000"
    },
    {
      title: "Marine & Transport Safety",
      code: "ISO 45001",
      desc: "Occupational Health & Safety for Liveaboards, Transfer Vessels & Staff",
      icon: <FaUsers />,
      image: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&w=800&q=80",
      className: "card-variant-1",
      id: "iso-45001"
    },
    {
      title: "Guest Data Security",
      code: "ISO 27001",
      desc: "Information & Booking System Security for Luxury Hospitality",
      icon: <FaCertificate />,
      image: "https://images.unsplash.com/photo-1614064641938-3bbee52942c7?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8c2VjdXJpdHl8ZW58MHx8MHx8fDA%3D",
      className: "card-variant-2",
      id: "iso-27001"
    },
    {
      title: "Island Sustainability",
      code: "ISO 50001",
      desc: "Energy Conservation & Efficient Power Management Systems",
      icon: <FaHandshake />,
      image: "https://images.unsplash.com/photo-1506953823976-52e1fdc0149a?auto=format&fit=crop&w=800&q=80",
      className: "card-variant-3",
      id: "iso-50001"
    }
  ];

  return (
    <>
      <Hero />

      {/* Moving Fluid Wavy Divider */}
      <div className="wavy-divider-container">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path 
            className="wavy-wave-path" 
            d="M0,0 C150,90 350,-40 500,40 C650,120 900,10 1200,50 L1200,120 L0,120 Z"
          ></path>
        </svg>
      </div>

      {/* Services Grid Section */}
      <section className="services-section section-padding scroll-trigger-view">
        <div className="container">
          <div className="section-heading style-heading-split reveal-on-scroll">
            <span className="section-tag tag-blue">MALDIVES COMPLIANCE</span>
            <h2>Our ISO Certification Services in the Maldives</h2>
            <p>
              Elevate your operations to global standards. We provide fully managed, 
              practical frameworks tailored perfectly to the unique demands of Maldivian enterprise.
            </p>
          </div>

          <div className="asymmetric-service-grid">
            {featuredServices.map((service, index) => (
              <div
                key={service.title}
                className={`kinetic-service-card ${service.className} reveal-on-scroll delay-${(index % 3) + 1}`}
                onClick={() => navigate(`/certifications/${service.id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") navigate(`/certifications/${service.id}`);
                }}
              >
                <div className="kinetic-card-background" style={{ backgroundImage: `url(${service.image})` }}></div>
                <div className="kinetic-card-overlay"></div>
                <span className="card-code-badge">{service.code}</span>
                
                <div className="kinetic-card-content">
                  <div className="kinetic-icon-box">{service.icon}</div>
                  <div className="kinetic-text-pane">
                    <h3>{service.title}</h3>
                    <p>{service.desc}</p>
                  </div>
                  <span className="kinetic-action-link">
                    Explore Framework <FaArrowRight className="kinetic-arrow" />
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="services-actions reveal-on-scroll">
            <button 
              onClick={() => navigate("/certifications")} 
              className="secondary-btn view-all-btn custom-gold-btn pulse-glow-btn"
            >
              View All Standards <FaExternalLinkAlt className="btn-icon-right" />
            </button>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="why-features-section section-padding scroll-trigger-view">
        <div className="container relative-z">
          <div className="section-heading wavy-heading-anim compact-heading reveal-on-scroll">
            <span className="section-tag tag-yellow">WHY CHOOSE US</span>
            <h2>Why Organizations Trust Us for ISO Certification</h2>
            <p>
              We bridge the gap between strict international ISO frameworks and the ground realities of 
              Maldivian business environments—delivering efficient, hassle-free audit execution.
            </p>
          </div>
        </div>

        <div className="why-marquee-wrapper reveal-on-scroll">
          <div className="why-marquee-track">
            {whyChooseUsFeatures.concat(whyChooseUsFeatures).map((feature, idx) => (
              <div 
                key={`${feature.title}-${idx}`} 
                className="why-feature-card glow-card-border"
              >
                <div className="why-card-image-wrap">
                  <img src={feature.image} alt={feature.title} className="why-card-img" />
                  <div className="why-card-gradient-overlay"></div>
                  <span className="why-card-badge-glass">{feature.highlight}</span>
                  <div className="why-floating-icon">{feature.icon}</div>
                </div>
                <div className="why-card-body">
                  <h3>{feature.title}</h3>
                  <p>{feature.desc}</p>
                  <div className="why-card-footer"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROVEN STRATEGY SECTION */}
      <section className="built-for-orgs-section section-padding scroll-trigger-view">
        <div className="container relative-z">
          <div className="section-heading reveal-on-scroll">
            <span className="section-tag tag-yellow">PROVEN STRATEGY</span>
            <h2>Built for organizations that want a smoother certification journey</h2>
            <p>
              We bridge the gap between complex global framework requirements and the reality of 
              Maldivian supply chains and operations, helping your company remain audit-ready year-round.
            </p>
          </div>

          <div className="proven-strategy-grid">
            <div className="proven-strategy-card reveal-on-scroll delay-1 hover-lift">
              <div className="proven-circle-image-wrap">
                <img
                  src="https://images.unsplash.com/photo-1573843981267-be1999ff37cd?auto=format&fit=crop&w=400&q=80"
                  alt="End-to-End Framework Implementation in Maldives"
                />
                <div className="proven-card-overlay-content">
                  <h3>End-to-End Framework Implementation</h3>
                  <p>Complete lifecycle oversight from initial operational gap analysis to final accredited certificate issuance.</p>
                </div>
              </div>
            </div>

            <div className="proven-strategy-card reveal-on-scroll delay-2 hover-lift">
              <div className="proven-circle-image-wrap">
                <img
                  src="https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&w=400&q=80"
                  alt="Localized Hospitality & Marine Templates Maldives"
                />
                <div className="proven-card-overlay-content">
                  <h3>Localized Hospitality & Marine Templates</h3>
                  <p>Custom manuals created explicitly to match island resort logistics, liveaboards, and regional compliance mandates.</p>
                </div>
              </div>
            </div>

            <div className="proven-strategy-card reveal-on-scroll delay-3 hover-lift">
              <div className="proven-circle-image-wrap">
                <img
                  src="https://images.unsplash.com/photo-1516815231560-8f41ec531527?auto=format&fit=crop&w=400&q=80"
                  alt="Practical Personnel Training Maldives"
                />
                <div className="proven-card-overlay-content">
                  <h3>Practical Personnel Training</h3>
                  <p>Engaging simulation exercises and role-based validation to ensure multi-national resort staff execute protocols fluently.</p>
                </div>
              </div>
            </div>

            <div className="proven-strategy-card reveal-on-scroll delay-4 hover-lift">
              <div className="proven-circle-image-wrap">
                <img
                  src="https://images.unsplash.com/photo-1512100356356-de1b84283e18?auto=format&fit=crop&w=400&q=80"
                  alt="Annual Surveillance Review Maldives"
                />
                <div className="proven-card-overlay-content">
                  <h3>Continuous Long-Term Support</h3>
                  <p>Year-round surveillance assistance, internal audit updates, and proactive compliance tracking to maintain accreditation.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUSTED ACROSS THE ATOLLS */}
      <section className="trusted-atolls-section section-padding scroll-trigger-view">
        <div className="container relative-z">
          <div className="section-heading style-heading-card-box reveal-on-scroll">
            <span className="section-tag tag-blue">TRUSTED ACROSS THE ATOLLS</span>
            <h2>Organizations that value reliability and compliance</h2>
            <p>
              We engineer functional frameworks for localized sectors requiring robust, 
              internationally accredited systems to stand out globally.
            </p>
          </div>

          <div className="atolls-bento-grid">
            <div className="bento-card bento-visual-card reveal-on-scroll delay-1">
              <div className="bento-img-container">
                <video
                  src={complianceVideo}
                  autoPlay
                  loop
                  muted
                  playsInline
                  aria-label="Maldivian Atoll ecosystem and resort aerial view"
                />
                <div className="bento-img-gradient"></div>
              </div>
              <div className="bento-visual-content">
                <span className="bento-mini-pill"><FaGlobeAsia /> Archipelago Reach</span>
                <h3>Atoll-Wide Compliance Infrastructure</h3>
                <p>Tailored auditing systems deployed seamlessly across remote island resorts and regional logistics hubs.</p>
              </div>
              <div className="bento-floating-stat">
                <h4>100%</h4>
                <span>Atoll Compliance</span>
              </div>
            </div>

            <div className="bento-card bento-feature-card reveal-on-scroll delay-2">
              <div className="bento-icon-box trust-blue">
                <FaRegBuilding />
              </div>
              <h3>Hospitality & Resort Focused</h3>
              <p>Structured blueprints explicitly optimized for remote resort environments, minimizing guest disruption and operational downtime.</p>
              <div className="bento-footer-tag">
                <FaCheckCircle className="bento-check" /> Zero Resort Downtime
              </div>
            </div>

            <div className="bento-card bento-feature-card reveal-on-scroll delay-3">
              <div className="bento-icon-box trust-yellow">
                <FaCogs />
              </div>
              <h3>Ecosystem Conscious Systems</h3>
              <p>Ensuring your operational policies seamlessly match the strict environmental mandates required to safeguard local marine biospheres.</p>
              <div className="bento-footer-tag">
                <FaCheckCircle className="bento-check" /> Biosphere Aligned
              </div>
            </div>
          </div>

          <div className="atolls-sector-subgrid reveal-on-scroll delay-4">
            <div className="sector-pill-card">
              <div className="sector-icon"><FaShip /></div>
              <div>
                <h4>Maldivian Hospitality</h4>
                <span>Resorts & Liveaboards</span>
              </div>
            </div>
            <div className="sector-pill-card">
              <div className="sector-icon"><FaHospital /></div>
              <div>
                <h4>Healthcare & Clinics</h4>
                <span>Medical Quality Systems</span>
              </div>
            </div>
            <div className="sector-pill-card">
              <div className="sector-icon"><FaIndustry /></div>
              <div>
                <h4>Logistics & Construction</h4>
                <span>Supply Chain Safety</span>
              </div>
            </div>
            <div className="sector-pill-card">
              <div className="sector-icon"><FaSchool /></div>
              <div>
                <h4>Educational Institutes</h4>
                <span>Institutional Protocols</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IMPLEMENTATION PIPELINE SECTION */}
      <section 
        className="pipeline-section-v2 section-padding scroll-trigger-view"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="container">
          <div className="section-heading wavy-heading-anim reveal-on-scroll">
            <span className="section-tag tag-blue">4-STAGE ISO ROADMAP</span>
            <h2>Our Active Implementation Pipeline</h2>
            <p>
              This roadmap automatically progresses quickly through each stage, or you can click any phase below to explore manually.
            </p>
          </div>

          <div className="pipeline-horizontal-stepper reveal-on-scroll">
            <div className="stepper-line-track">
              <div 
                className="stepper-line-progress" 
                style={{ width: `${(activeTab / (processSteps.length - 1)) * 100}%` }}
              ></div>
            </div>

            <div className="stepper-nodes-wrap">
              {processSteps.map((s, idx) => {
                const isActive = activeTab === idx;
                const isCompleted = activeTab > idx;
                return (
                  <button
                    key={s.step}
                    className={`stepper-node-btn ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}
                    onClick={() => setActiveTab(idx)}
                  >
                    <div className="node-circle">
                      {isCompleted ? <FaCheckCircle /> : <span>{s.step}</span>}
                    </div>
                    <span className="node-title">{s.shortTitle}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="pipeline-stage-showcase reveal-on-scroll animated-border-card">
            <div className="showcase-visual-side">
              <img 
                src={processSteps[activeTab].image} 
                alt={processSteps[activeTab].title} 
                className="showcase-img"
              />
              <div className="showcase-img-overlay"></div>
              <span className="showcase-badge-glass">
                <FaRocket className="badge-icon pulse-icon-slow" /> {processSteps[activeTab].badge}
              </span>
              <div className="showcase-floating-step-tag">
                <span>Phase</span>
                <h3>{processSteps[activeTab].step}</h3>
              </div>
            </div>

            <div className="showcase-content-side">
              <div className="showcase-header">
                <div className="showcase-icon-box">
                  {processSteps[activeTab].icon}
                </div>
                <div>
                  <span className="showcase-sub-tag">Stage {processSteps[activeTab].step} of 04</span>
                  <h3>{processSteps[activeTab].title}</h3>
                </div>
              </div>

              <p className="showcase-description">{processSteps[activeTab].desc}</p>

              <div className="showcase-features-list">
                {processSteps[activeTab].features.map((feat, fIdx) => (
                  <div key={fIdx} className="showcase-feature-item">
                    <FaCheckCircle className="feat-check" />
                    <span>{feat}</span>
                  </div>
                ))}
              </div>

              <div className="showcase-actions">
                <button 
                  className="primary-btn pipeline-action-btn shimmer-btn"
                  onClick={() => navigate("/contact")}
                >
                  Start Phase {processSteps[activeTab].step} <FaArrowRight />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="stats-section section-padding scroll-trigger-view">
        <div className="container stats-grid">
          <div className="stat-box stat-blue reveal-on-scroll delay-1 glow-on-hover">
            <h2>3230+</h2>
            <p>International Clients</p>
          </div>
          <div className="stat-box stat-red reveal-on-scroll delay-2 glow-on-hover">
            <h2>1400+</h2>
            <p>India Clients</p>
          </div>
          <div className="stat-box stat-yellow reveal-on-scroll delay-3 glow-on-hover">
            <h2>62+</h2>
            <p>Expert Team Members</p>
          </div>
          <div className="stat-box stat-teal reveal-on-scroll delay-4 glow-on-hover">
            <h2>99.6%</h2>
            <p>Client Satisfaction</p>
          </div>
        </div>
      </section>

      {/* Featured Client Partners Infinite Auto-Scroll Section */}
      <section className="featured-clients-section light-clients-theme section-padding scroll-trigger-view">
        <div className="container">
          <div className="section-heading wavy-heading-anim reveal-on-scroll dark-heading-text">
            <span className="section-tag tag-blue">TRUSTED RELATIONSHIPS</span>
            <h2>Featured Client Partners</h2>
            <p>
              Over <strong>3230+</strong> international enterprise networks and recognized localized brands supported with 
              uncompromising ISO execution architectures.
            </p>
          </div>
        </div>

        <div className="client-marquee-wrapper reveal-on-scroll delay-2">
          <div className="client-marquee-track">
            {prestigiousClients.map((client, idx) => (
              <div className="client-logo-card card-light-variant" key={`orig-${idx}`}>
                <img src={client.logo} alt={`${client.name} logo`} />
                <span className="client-hover-name">{client.name}</span>
              </div>
            ))}
            {prestigiousClients.map((client, idx) => (
              <div className="client-logo-card card-light-variant" key={`dup-${idx}`}>
                <img src={client.logo} alt={`${client.name} logo`} />
                <span className="client-hover-name">{client.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* YouTube Channel & Video Showcase Section */}
      <section className="youtube-showcase-section section-padding scroll-trigger-view">
        <div className="container">
          <div className="section-heading wavy-heading-anim reveal-on-scroll">
            <span className="section-tag tag-red"><FaYoutube className="yt-inline-icon" /> YOUTUBE CHANNEL</span>
            <h2>Explore Our Insights & Video Guides</h2>
            <p>
              Watch our expert walkthroughs, ISO implementation overviews, and food safety training guides on our official YouTube channel.
            </p>
          </div>

          <div className="youtube-videos-grid reveal-on-scroll delay-2">
            {channelVideos.map((vid, i) => (
              <a 
                href={vid.url} 
                target="_blank" 
                rel="noopener noreferrer" 
                key={i} 
                className="yt-video-card"
              >
                <div className="yt-thumb-wrapper">
                  <img 
                    src={`https://img.youtube.com/vi/${vid.videoId}/hqdefault.jpg`} 
                    alt={vid.title} 
                  />
                  <div className="yt-play-overlay">
                    <FaPlayCircle className="yt-play-icon" />
                  </div>
                  <span className="yt-duration-badge">{vid.duration}</span>
                </div>
                <div className="yt-card-content">
                  <h4>{vid.title}</h4>
                  <span className="yt-channel-link-text">
                    <FaYoutube /> Watch on YouTube <FaExternalLinkAlt className="external-arr" />
                  </span>
                </div>
              </a>
            ))}
          </div>

          <div className="youtube-channel-action reveal-on-scroll delay-3">
            <a 
              href="https://www.youtube.com/@Ascent_Inspecta" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="primary-btn yt-channel-btn shimmer-btn"
            >
              <FaYoutube className="btn-yt-icon" /> Visit @Ascent_Inspecta Channel <FaExternalLinkAlt />
            </a>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="faq-section-modern section-padding scroll-trigger-view">
        <div className="container">
          <div className="faq-modern-layout">
            <div className="faq-modern-sidebar reveal-on-scroll">
              <span className="section-tag tag-blue"><FaQuestionCircle /> HELP CENTER</span>
              <h2>Frequently Asked Questions</h2>
              <p>
                Got questions about our audit process or Maldivian compliance requirements? 
                Explore our answers below or reach out to our team directly.
              </p>
              <div className="faq-sidebar-pill">
                <strong>Need immediate assistance?</strong>
                <span>Our compliance desk responds within 2 hours.</span>
              </div>
            </div>

            <div className="faq-modern-content-wrap reveal-on-scroll delay-1">
              {faqData.map((item, index) => (
                <FaqItem
                  key={item.question}
                  item={item}
                  index={index}
                  openIndex={openIndex}
                  setOpenIndex={setOpenIndex}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Auto-Rotating & Interactive Carousel */}
      <section className="testimonial-section-v2 section-padding scroll-trigger-view">
        <div className="container relative-z">
          <div className="section-heading wavy-heading-anim compact-heading reveal-on-scroll">
            <span className="section-tag tag-blue"><FaStar /> CLIENT TRUST & VOICES</span>
            <h2>Professional support that organizations value</h2>
            <p>Discover real-world outcomes and feedback from leaders navigating compliance across the Maldives.</p>
          </div>

          <div className="reviews-carousel-wrapper reveal-on-scroll delay-1">
            {/* Carousel Active Main Card */}
            <div className="review-spotlight-card hover-lift">
              <div className="spotlight-top-badge">
                <span className="spotlight-pill"><FaAward /> Verified Feedback</span>
                <div className="star-rating">
                  {[...Array(googleReviews[activeReviewIndex].rating)].map((_, i) => (
                    <FaStar key={i} />
                  ))}
                </div>
              </div>
              <FaQuoteLeft className="spotlight-quote-icon" />
              <p className="spotlight-quote-text">
                “{googleReviews[activeReviewIndex].text}”
              </p>
              <div className="spotlight-profile-wrap">
                <div className="spotlight-avatar">
                  {googleReviews[activeReviewIndex].author.charAt(0)}
                </div>
                <div className="spotlight-meta">
                  <h4>{googleReviews[activeReviewIndex].author}</h4>
                  <span>{googleReviews[activeReviewIndex].role}</span>
                </div>
              </div>
            </div>

            {/* Carousel Interactive Controls */}
            <div className="carousel-controls-bar">
              <button 
                className="carousel-control-btn"
                onClick={() => setActiveReviewIndex((prev) => (prev === 0 ? googleReviews.length - 1 : prev - 1))}
                aria-label="Previous Review"
              >
                &larr; Prev
              </button>

              <div className="carousel-dots">
                {googleReviews.map((_, dotIdx) => (
                  <button
                    key={dotIdx}
                    className={`carousel-dot ${dotIdx === activeReviewIndex ? "active" : ""}`}
                    onClick={() => setActiveReviewIndex(dotIdx)}
                    aria-label={`Go to slide ${dotIdx + 1}`}
                  />
                ))}
              </div>

              <button 
                className="carousel-control-btn"
                onClick={() => setActiveReviewIndex((prev) => (prev + 1) % googleReviews.length)}
                aria-label="Next Review"
              >
                Next &rarr;
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="cta-section section-padding scroll-trigger-view">
        <div className="container cta-box reveal-on-scroll animated-cta-gradient">
          <div className="cta-info">
            <span className="section-tag tag-yellow">GET STARTED</span>
            <h2>Ready to Certify Your Maldivian Enterprise?</h2>
            <p>Let's unlock top-tier compliance efficiency with a seamless roadmap mapped directly around your operation.</p>
          </div>

          <div className="cta-actions">
            <button className="primary-btn shimmer-btn" onClick={() => navigate("/contact")}>Request Free Consultation</button>
            <button className="secondary-btn light-btn">Call Now</button>
          </div>
        </div>
      </section>

      {/* Fixed Sticky Dynamic Badge */}
      <div className="floating-note floating-badge-anim">
        <FaMapMarkerAlt className="pulse-icon" />
        <span>Maldives ISO Certification Support</span>
      </div>

      {/* ================= CORNER WIDGETS (WHATSAPP & CHATBOT) ================= */}
      
      {/* 1. WhatsApp Floating Widget (+91 9867180395) */}
      <a
        href="https://wa.me/919867180395?text=Hello,%20I%20would%20like%20to%20know%20more%20about%20ISO%20certification%20services."
        className="whatsapp-float-btn"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with us on WhatsApp"
      >
        <FaWhatsapp className="whatsapp-icon" />
        <span className="whatsapp-tooltip">Chat on WhatsApp</span>
      </a>

      {/* 2. Interactive Chatbot Floating System */}
      <div className="chatbot-wrapper">
        <button
          className="chatbot-toggle-btn"
          onClick={() => setIsChatOpen(!isChatOpen)}
          aria-label="Toggle Support Chat"
        >
          {isChatOpen ? <FaTimes /> : <FaComments />}
          {!isChatOpen && <span className="chatbot-badge-pulse">1</span>}
        </button>

        {isChatOpen && (
          <div className="chatbot-window">
            <div className="chatbot-header">
              <div className="chatbot-title-area">
                <span className="chatbot-status-dot"></span>
                <div>
                  <h4>Ascent Support Desk</h4>
                  <p>Online | Replies instantly</p>
                </div>
              </div>
              <button className="chatbot-close-btn" onClick={() => setIsChatOpen(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="chatbot-messages-body">
              {chatMessages.map((msg, index) => (
                <div key={index} className={`chat-bubble ${msg.sender}`}>
                  <p>{msg.text}</p>
                </div>
              ))}
            </div>

            <div className="chatbot-quick-actions">
              <a 
                href="https://wa.me/919867180395?text=Hello,%20I%20need%20urgent%20assistance%20with%20ISO%20certification." 
                target="_blank" 
                rel="noopener noreferrer"
                className="chatbot-quick-whatsapp-link"
              >
                <FaWhatsapp /> Chat on WhatsApp (+91 9867180395)
              </a>
            </div>

            <form className="chatbot-input-footer" onSubmit={handleSendMessage}>
              <input
                type="text"
                placeholder="Type your message here..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
              />
              <button type="submit" aria-label="Send message">
                <FaPaperPlane />
              </button>
            </form>
          </div>
        )}
      </div>
    </>
  );
}

export default Home;